import { forwardRef, useEffect, useRef, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useAuthStore } from "@/services/Cognito/authStore";
import { useMutation } from "@tanstack/react-query";
import { twMerge } from "tailwind-merge";
import { useOnClickOutside } from "usehooks-ts";

import { LoadingSpinner } from "./assets_old/LoadingSpinner";

type SubscriptionTier = "Annual" | "Monthly";

type SubscriptionTierProps = {
  title: string;
  price: string;
  description: Array<string>;
  handleClickButton: () => void;
  className?: string;
  buttonClassName?: string;
  loadingState: SubscriptionTier | null;
  spinnerClassName: string;
  discount?: ReactNode;
};

const PricingCard = (props: SubscriptionTierProps) => {
  const baseContainerClass =
    "flex  w-full max-w-[250px] flex-col rounded-xl border bg-violet-100 px-1 shadow-2xl sm:px-4 relative";
  const baseButtonClass =
    "text-sm font-bold sm:text-base py-1 rounded h-8 w-full grid justify-center items-center";
  const loading = props.loadingState === props.title;
  return (
    <div className="flex select-none justify-center">
      <div className={twMerge(baseContainerClass, props.className)}>
        <h2 className="pt-2 text-lg font-semibold">{props.title}</h2>
        {props.discount}
        <h1 className="py-3 text-4xl font-extrabold ">{props.price}</h1>
        <div className="flex h-full flex-col justify-between">
          <div className="flex flex-col gap-3 pb-2 pt-2 text-left">
            {props.description.map((bullet) => (
              <div
                className="flex"
                key={`pricing-card-${props.title}-{-${bullet}`}
              >
                <span className="pr-1">•</span>
                <span>{bullet}</span>
              </div>
            ))}
          </div>
          <div className="pb-1 sm:pb-4">
            <button
              className={twMerge(props.buttonClassName, baseButtonClass)}
              onClick={props.handleClickButton}
            >
              {loading ? (
                <LoadingSpinner className={props.spinnerClassName} />
              ) : (
                <span>Subscribe Now</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SubscribeView = forwardRef<HTMLDivElement, { hide: () => void }>(
  function SubView(props, ref) {
    const router = useRouter();
    const [loading, setLoading] = useState<SubscriptionTier | null>(null);
    const { mutate, isLoading } = useMutation({
      mutationFn: async (tier: SubscriptionTier) => {
        setLoading(tier);
        const routeURL = new URL(
          "api/stripe/createSubscriptionCheckoutSession",
          location.origin,
        );
        routeURL.searchParams.append("success_url", `${window.location.href}`);
        routeURL.searchParams.append("cancel_url", `${window.location.href}`);
        routeURL.searchParams.append("subscription_tier", tier);

        const res = await fetch(routeURL, {
          method: "POST",
          cache: "no-cache",
        });
        return (await res.json()) as
          | { session_url: string }
          | { error: string; status: number };
      },
      onSuccess: (data) => {
        setLoading(null);
        if ("session_url" in data) router.push(data.session_url);
        else console.error(data.error);
      },
    });

    const handleCheckout = (tier: SubscriptionTier) => {
      return () => {
        if (!isLoading) mutate(tier);
      };
    };

    return (
      <div
        ref={ref}
        className="relative grid max-w-[750px] grid-cols-2  gap-2 rounded-lg bg-white p-5"
      >
        <button
          onClick={props.hide}
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-[#E8E8E8] "
        >
          <span className="pb-1 text-xl font-semibold text-[#979797]">×</span>
        </button>
        <h2 className="col-span-3 text-3xl font-semibold">
          Celebrate all of life&apos;s special moments!
        </h2>
        <div className="col-span-3 pb-1 text-center text-neutral-600 sm:px-4">
          <span className="text-2xl"> Membership includes:</span>
        </div>
        <PricingCard
          spinnerClassName="w-5 h-5 ml-2.5"
          handleClickButton={handleCheckout("Monthly")}
          buttonClassName="bg-violet-500 hover:bg-violet-600 text-white"
          title={"Monthly"}
          price="$4"
          description={[
            "Easily customize eCards",
            "Send original art",
            "Celebrate sustainably",
            "Support our women owned small business",
            "Unlimited cards monthly for the cost one paper card!",
          ]}
          loadingState={loading}
        />
        <PricingCard
          discount={
            <span className="absolute left-0.5 top-8 w-full text-center text-xs text-amber-300">
              Save 33%
            </span>
          }
          spinnerClassName="w-5 h-5 ml-2.5 text-violet-700"
          handleClickButton={handleCheckout("Annual")}
          loadingState={loading}
          title={"Annual"}
          price="$24"
          buttonClassName="bg-white hover:bg-violet-50 text-violet-700"
          className="bg-violet-700 text-white ring-4 ring-amber-400"
          description={[
            "Easily customize eCards",
            "Send original art",
            "Celebrate sustainably",
            "Support our women owned small business",
            "Best value! ⭐",
          ]}
        />
        <div className="col-span-3 text-xs font-thin">
          <span>
            All memberships renew automatically. To cancel, simply click the{" "}
          </span>
          <span className="font-semibold">Manage Membership </span>
          <span>button on your profile page.</span>
        </div>
      </div>
    );
  },
);

export const SubscribeModal = ({
  visible,
  hide,
}: {
  visible: boolean;
  hide: () => void;
}) => {
  const user = useAuthStore((ctx) => ctx.user);
  const ref = useRef(null);
  useOnClickOutside(ref, hide);
  useEffect(() => {
    // prevent scrollings when modal is open
    if (visible) document.body.style.overflow = "hidden";

    return function () {
      document.body.style.overflow = "unset";
    };
  }, [visible]);

  if (!visible) return null;
  if (
    user?.stripeSubscriptionValidUntil &&
    user.stripeSubscriptionValidUntil.getTime() > new Date().getTime()
  )
    return null;

  return (
    <div
      className="relative"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 60 }}
    >
      <div className="fixed inset-0 h-full bg-gray-500 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="box-shadow flex min-h-full items-end justify-center p-4 text-center sm:items-center ">
          <SubscribeView ref={ref} hide={hide} />
        </div>
      </div>
    </div>
  );
};
